import {
  GuidedNbaItem,
  GuidedNBATile,
} from '../../components/GuidedNBACarousel/types'
import {
  ApplicableFilter,
  FilterItem,
  SearchFilter,
} from '../../services/types/services'
import { getFiltersById } from '../../store/helpers'
import { buildGuidedNbaTileUrl } from './buildGuidedNbaTileUrl'

type FilterRanking = {
  nbaScore?: number
  count: number
}

const getFacetRanking = (
  item: GuidedNbaItem,
  preAppliedKeys: Set<string>,
  lookup: Map<string, FilterRanking>,
) => {
  const facetKey = item.facetDetails.find(facet => !preAppliedKeys.has(facet))

  return facetKey ? lookup.get(facetKey) : undefined
}

const buildFilterRankingLookup = (
  filters: FilterItem[],
): Map<string, FilterRanking> => {
  const lookup = new Map<string, FilterRanking>()
  const indexFilterItems = (items: FilterItem[]) => {
    items.forEach(item => {
      if (
        item.__typename === 'Filter' ||
        item.__typename === 'FilterCategory'
      ) {
        lookup.set(item.id, {
          nbaScore: item.nbaScore,
          count: item.count,
        })
      }
      if (item.__typename === 'FilterCategory' && item.children) {
        indexFilterItems(item.children)
      }
    })
  }

  indexFilterItems(filters)
  return lookup
}

const orderByNbaScore = (
  rankA: FilterRanking | undefined,
  rankB: FilterRanking | undefined,
): number | undefined => {
  const nbaA = rankA?.nbaScore
  const nbaB = rankB?.nbaScore

  if (nbaA != null && nbaB != null && nbaA !== nbaB) {
    return nbaA - nbaB
  }
  if (nbaA != null && nbaB == null) return -1
  if (nbaA == null && nbaB != null) return 1

  return undefined
}

const orderByCount = (
  rankA: FilterRanking | undefined,
  rankB: FilterRanking | undefined,
): number | undefined => {
  const countA = rankA?.count ?? /* istanbul ignore next */ 0
  const countB = rankB?.count ?? /* istanbul ignore next */ 0

  return countA !== countB ? countB - countA : undefined
}

const orderByCmsIndex = (cmsIndexA: number, cmsIndexB: number): number =>
  cmsIndexA - cmsIndexB

const toApplicableFilter = (
  filter: SearchFilter,
): Partial<ApplicableFilter> => ({
  id: filter.key,
  parent: filter.group,
})

const getPreAppliedFiltersMatchingFacets = (
  facetIds: string[],
  preAppliedFilters: SearchFilter[] | undefined,
): Partial<ApplicableFilter>[] => {
  if (!preAppliedFilters?.length) {
    return []
  }

  const facetIdSet = new Set(facetIds)

  return preAppliedFilters
    .filter(filter => facetIdSet.has(filter.key))
    .map(toApplicableFilter)
}

const getDestinationFilters = (
  filterIds: string[],
  allFilters: FilterItem[],
  preAppliedFilters: SearchFilter[] | undefined,
): ApplicableFilter[] => {
  const filtersFromTree = getFiltersById({ filterIds, allFilters })
  const preAppliedMatchingFacets = getPreAppliedFiltersMatchingFacets(
    filterIds,
    preAppliedFilters,
  )

  return [...filtersFromTree, ...preAppliedMatchingFacets] as ApplicableFilter[]
}

export const orderGuidedNbaItems = ({
  sourceUrl,
  items,
  filters,
  preAppliedFilters,
}: {
  sourceUrl: string
  items: GuidedNbaItem[]
  filters: FilterItem[]
  preAppliedFilters?: SearchFilter[]
}): GuidedNBATile[] => {
  const lookup = buildFilterRankingLookup(filters)
  const preAppliedKeys = new Set(
    preAppliedFilters?.map(filter => filter.key) ?? [],
  )

  return items
    .map((item, cmsIndex) => ({ item, cmsIndex }))
    .sort((a, b) => {
      const rankA = getFacetRanking(a.item, preAppliedKeys, lookup)
      const rankB = getFacetRanking(b.item, preAppliedKeys, lookup)

      return (
        orderByNbaScore(rankA, rankB) ??
        orderByCount(rankA, rankB) ??
        orderByCmsIndex(a.cmsIndex, b.cmsIndex)
      )
    })
    .map(({ item }) => ({
      image: item.tile.image.url,
      link: buildGuidedNbaTileUrl({
        sourceUrl,
        destinationUrl: item.tile.url,
        sourcePreAppliedFilters:
          preAppliedFilters?.map(toApplicableFilter) ?? [],
        destinationFilters: getDestinationFilters(
          item.facetDetails,
          filters,
          preAppliedFilters,
        ),
      }),
      title: item.tile.title,
    }))
}
